/**
 * ConsentModal Component
 *
 * Displays the 給与明細書等電子交付同意書 (Electronic Delivery Consent Form for Payslips)
 * as a modal. Shown on first login until the user agrees or declines.
 */

import React from 'react';
import './ConsentModal.css';
import { CONSENT_APP_URL } from './consentFormShared';
import type { ResolvedConsentFormConfig } from './consentFormTypes';

/** Admin: edit body in-place (same layout as preview). */
export interface ConsentModalInlineEdit {
  sectionTexts: string[];
  onSectionTextChange: (sectionIndex: number, value: string) => void;
  printLine1: string;
  printLine2: string;
  onPrintLineChange: (which: 1 | 2, value: string) => void;
  disabled?: boolean;
}

export interface ConsentModalProps {
  empCode: string;
  name: string;
  /** Resolved template from API (or client fallback). */
  config: ResolvedConsentFormConfig;
  onAgree: () => void;
  onDisagree: () => void;
  submitting?: boolean;
  /** Embedded preview (admin settings): no full-screen overlay. */
  variant?: 'modal' | 'embedded';
  /** When false, hide PDF + agree/disagree (preview only). */
  showFooterActions?: boolean;
  /** When set, 「記」〜「以上」and footnote render as textareas in the same positions as read-only lines. */
  inlineEdit?: ConsentModalInlineEdit;
}

export const ConsentModal: React.FC<ConsentModalProps> = ({
  empCode,
  name,
  config,
  onAgree,
  onDisagree,
  submitting = false,
  variant = 'modal',
  showFooterActions = true,
  inlineEdit,
}) => {
  const box = (
    <div
      className={`consent-modal-box ${variant === 'embedded' ? 'consent-modal-box--embedded' : ''}${
        inlineEdit ? ' consent-modal-box--inline-edit' : ''
      }`}
    >
      <h2 id="consent-modal-title" className="consent-modal-title">
        給与明細書等電子交付同意書
      </h2>

      <p className="consent-modal-intro">
        私は、給与明細書等の電子交付について、以下の内容に同意いたします。
      </p>

      <div className="consent-modal-fields">
        <div className="consent-field">
          <span className="consent-field-label">■社員番号：</span>
          <span className="consent-field-value">{empCode}</span>
        </div>
        <div className="consent-field">
          <span className="consent-field-label">■氏名：</span>
          <span className="consent-field-value">{name}</span>
        </div>
      </div>

      <div className="consent-modal-section">
        <div className="consent-section-label">記</div>

        {config.sections.map((sec, si) => (
          <div key={`${sec.title}-${si}`} className="consent-subsection">
            <div className="consent-subsection-title">{sec.title}</div>
            {inlineEdit ? (
              <textarea
                className="consent-modal-inline-textarea"
                value={inlineEdit.sectionTexts[si] ?? ''}
                onChange={(e) => inlineEdit.onSectionTextChange(si, e.target.value)}
                rows={sec.title.includes('重要事項') ? 6 : 5}
                disabled={inlineEdit.disabled}
                aria-label={`${sec.title}の本文`}
              />
            ) : (
              sec.lines.map((line, li) => {
                const isUrlLine = line.includes(CONSENT_APP_URL);
                return (
                  <p
                    key={li}
                    className={isUrlLine ? 'consent-url consent-doc-line' : 'consent-doc-line'}
                  >
                    {line}
                  </p>
                );
              })
            )}
          </div>
        ))}
      </div>

      <p className="consent-modal-closing">以上</p>

      {(inlineEdit || config.printNoticeLines.join('').trim()) && (
      <div
        className={`consent-print-notice${inlineEdit ? ' consent-print-notice--inline-edit' : ''}`}
        aria-live="polite"
      >
        {inlineEdit ? (
          <>
            <textarea
              className="consent-modal-inline-textarea consent-modal-inline-textarea--footnote"
              value={inlineEdit.printLine1}
              onChange={(e) => inlineEdit.onPrintLineChange(1, e.target.value)}
              rows={2}
              disabled={inlineEdit.disabled}
              aria-label="「以上」の下の注記（前半）"
            />
            <textarea
              className="consent-modal-inline-textarea consent-modal-inline-textarea--footnote"
              value={inlineEdit.printLine2}
              onChange={(e) => inlineEdit.onPrintLineChange(2, e.target.value)}
              rows={2}
              disabled={inlineEdit.disabled}
              aria-label="「以上」の下の注記（後半）"
            />
            <p className="consent-print-notice-edit-hint">※保存時は上記2欄が連結されて表示されます（改行なし）。</p>
          </>
        ) : (
          <p className="consent-print-notice-body">{config.printNoticeLines.join('')}</p>
        )}
      </div>
      )}

      {showFooterActions && (
        <>
          <div className="consent-modal-actions">
            <button
              type="button"
              className="consent-btn consent-btn-agree"
              onClick={onAgree}
              disabled={submitting}
            >
              {submitting ? '送信中...' : '同意する'}
            </button>
            <button
              type="button"
              className="consent-btn consent-btn-disagree"
              onClick={onDisagree}
              disabled={submitting}
            >
              同意しない
            </button>
          </div>
        </>
      )}
    </div>
  );

  if (variant === 'embedded') {
    return (
      <div className="consent-modal-embedded-wrap" role="region" aria-label="同意書プレビュー">
        {box}
      </div>
    );
  }

  return (
    <div className="consent-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="consent-modal-title">
      {box}
    </div>
  );
};
